import fs from 'fs-extra';
import path from 'path';
import { WORKFLOW_DIR } from '../../constants.js';
// Simple ID generation function
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

export type PromptCategory = 
  | 'system'      // 系统提示词
  | 'task'        // 任务提示词
  | 'thinking'    // 思考提示词
  | 'output'      // 输出格式
  | 'code'        // 代码生成
  | 'review'      // 审查提示词
  | 'custom'      // 自定义
  | 'general'     // 通用
  | 'coding'      // 编程
  | 'writing'     // 写作
  | 'analysis'    // 分析
  | 'creative';   // 创意

export type PromptVariableValue = string | number | boolean | string[] | Record<string, unknown>;

export interface PromptVariable {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description: string;
  required: boolean;
  defaultValue?: PromptVariableValue;
  examples?: PromptVariableValue[];
}

export interface Prompt {
  id: string;
  name: string;
  description: string;
  category: PromptCategory;
  content: string;
  variables: PromptVariable[];
  tags: string[];
  author: string;
  version: string;
  isPublic: boolean;
  usageCount: number;
  rating?: number;
  examples?: Array<{
    input: Record<string, PromptVariableValue>;
    output: string;
  }>;
  created: string;
  updated: string;
}

export class PromptService {
  private promptsPath: string;

  constructor() {
    this.promptsPath = path.join(process.cwd(), WORKFLOW_DIR, 'prompts.json');
    this.ensurePromptsFile();
  }

  private async ensurePromptsFile() {
    await fs.ensureFile(this.promptsPath);
    const exists = await fs.pathExists(this.promptsPath);
    if (!exists || (await fs.readFile(this.promptsPath, 'utf-8')).trim() === '') {
      // 初始化一些默认 Prompt
      const defaultPrompts: Prompt[] = [
        {
          id: 'prompt-system-gongfu',
          name: 'Gongfu 系统提示词',
          description: 'Gongfu 项目的标准系统提示词',
          category: 'system',
          content: `你是 Gongfu 项目的 AI 助手，专注于帮助用户完成工作流管理和任务编排。

核心原则：
1. 始终使用中文回答
2. 保持简洁和专业
3. 提供可执行的建议
4. 遵循项目规范

项目信息：
- 名称：{{projectName}}
- 描述：{{projectDescription}}
- 技术栈：{{techStack}}`,
          variables: [
            {
              name: 'projectName',
              type: 'string',
              description: '项目名称',
              required: true,
              defaultValue: 'Gongfu'
            },
            {
              name: 'projectDescription',
              type: 'string',
              description: '项目描述',
              required: false,
              defaultValue: 'AI 驱动的工作流管理系统'
            },
            {
              name: 'techStack',
              type: 'array',
              description: '技术栈',
              required: false,
              defaultValue: ['TypeScript', 'React', 'Node.js']
            }
          ],
          tags: ['system', 'gongfu', 'standard'],
          author: '@admin',
          version: '1.0.0',
          isPublic: true,
          usageCount: 0,
          rating: 5,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'prompt-task-decompose',
          name: '任务分解提示词',
          description: '将复杂任务分解为可执行的小任务',
          category: 'task',
          content: `请将以下任务分解为具体的可执行步骤：

任务名称：{{taskName}}
任务描述：{{taskDescription}}
预期时间：{{estimatedTime}}

分解要求：
1. 每个子任务应该是独立可执行的
2. 时间估算要合理
3. 明确每个任务的输入和输出
4. 标注任务优先级

输出格式：
- 任务名称
- 任务描述
- 预计时间
- 优先级（高/中/低）
- 依赖关系`,
          variables: [
            {
              name: 'taskName',
              type: 'string',
              description: '任务名称',
              required: true
            },
            {
              name: 'taskDescription',
              type: 'string',
              description: '任务描述',
              required: true
            },
            {
              name: 'estimatedTime',
              type: 'string',
              description: '预期完成时间',
              required: false,
              defaultValue: '未指定'
            }
          ],
          tags: ['task', 'decomposition', 'planning'],
          author: '@admin',
          version: '1.0.0',
          isPublic: true,
          usageCount: 0,
          rating: 4.5,
          examples: [
            {
              input: {
                taskName: '实现用户登录功能',
                taskDescription: '使用 JWT 实现安全的用户登录系统',
                estimatedTime: '3天'
              },
              output: `任务分解如下：

1. 设计数据库结构
   - 描述：设计用户表和认证相关的数据结构
   - 预计时间：2小时
   - 优先级：高
   - 依赖：无

2. 实现后端 API
   - 描述：实现登录、注册、token 刷新等接口
   - 预计时间：1天
   - 优先级：高
   - 依赖：任务1

3. 实现前端界面
   - 描述：创建登录页面和相关组件
   - 预计时间：0.5天
   - 优先级：高
   - 依赖：任务2

4. 集成测试
   - 描述：编写测试用例并进行集成测试
   - 预计时间：0.5天
   - 优先级：中
   - 依赖：任务3`
            }
          ],
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'prompt-code-review',
          name: '代码审查提示词',
          description: '对代码进行专业的审查和建议',
          category: 'review',
          content: `请对以下代码进行审查：

编程语言：{{language}}
代码目的：{{purpose}}

代码：
\`\`\`{{language}}
{{code}}
\`\`\`

审查要点：
1. 代码质量和可读性
2. 潜在的 bug 和安全问题
3. 性能优化建议
4. 最佳实践遵循情况
5. 测试覆盖建议

请提供具体的改进建议和示例代码。`,
          variables: [
            {
              name: 'language',
              type: 'string',
              description: '编程语言',
              required: true,
              examples: ['javascript', 'typescript', 'python', 'java']
            },
            {
              name: 'purpose',
              type: 'string',
              description: '代码目的',
              required: true
            },
            {
              name: 'code',
              type: 'string',
              description: '待审查的代码',
              required: true
            }
          ],
          tags: ['code', 'review', 'quality'],
          author: '@admin',
          version: '1.0.0',
          isPublic: true,
          usageCount: 0,
          rating: 4.8,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'prompt-prd-generate',
          name: 'PRD 生成提示词',
          description: '根据需求生成产品需求文档',
          category: 'writing',
          content: `请根据以下信息生成产品需求文档（PRD）：

产品名称：{{productName}}
产品描述：{{productDescription}}
目标用户：{{targetUsers}}
核心功能：{{coreFeatures}}

PRD 应包含：
1. 产品概述
2. 背景和目标
3. 用户故事
4. 功能需求
5. 非功能需求
6. 成功指标
7. 时间规划

请使用 Markdown 格式输出。`,
          variables: [
            {
              name: 'productName',
              type: 'string',
              description: '产品名称',
              required: true
            },
            {
              name: 'productDescription',
              type: 'string',
              description: '产品描述',
              required: true
            },
            {
              name: 'targetUsers',
              type: 'string',
              description: '目标用户群体',
              required: true
            },
            {
              name: 'coreFeatures',
              type: 'array',
              description: '核心功能列表',
              required: true
            }
          ],
          tags: ['prd', 'product', 'documentation'],
          author: '@admin',
          version: '1.0.0',
          isPublic: true,
          usageCount: 0,
          rating: 4.6,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'prompt-thinking-flow',
          name: '思维链提示词',
          description: '引导 AI 进行深度思考和推理',
          category: 'thinking',
          content: `让我们一步步思考这个问题：

问题：{{question}}
背景信息：{{context}}

思考步骤：
1. 首先，理解问题的核心是什么
2. 其次，分析可能的解决方案
3. 然后，评估每个方案的优缺点
4. 最后，给出最佳建议和理由

请详细展示你的思考过程。`,
          variables: [
            {
              name: 'question',
              type: 'string',
              description: '需要思考的问题',
              required: true
            },
            {
              name: 'context',
              type: 'string',
              description: '背景信息',
              required: false,
              defaultValue: '无特定背景'
            }
          ],
          tags: ['thinking', 'reasoning', 'analysis'],
          author: '@admin',
          version: '1.0.0',
          isPublic: true,
          usageCount: 0,
          rating: 4.9,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        }
      ];
      await fs.writeJson(this.promptsPath, defaultPrompts, { spaces: 2 });
    }
  }

  async list(): Promise<Prompt[]> {
    try {
      const prompts = await fs.readJson(this.promptsPath);
      return prompts;
    } catch (error) {
      console.error('Error reading prompts:', error);
      return [];
    }
  }

  async get(id: string): Promise<Prompt | null> {
    const prompts = await this.list();
    return prompts.find(prompt => prompt.id === id) || null;
  }

  async create(promptData: Omit<Prompt, 'id' | 'created' | 'updated' | 'usageCount'>): Promise<Prompt> {
    const prompts = await this.list();
    
    // 检查名称是否重复
    if (prompts.some(prompt => prompt.name === promptData.name)) {
      throw new Error('Prompt 名称已存在');
    }

    const newPrompt: Prompt = {
      ...promptData,
      id: `prompt-${generateId()}`,
      usageCount: 0,
      created: new Date().toISOString(),
      updated: new Date().toISOString()
    };

    prompts.push(newPrompt);
    await fs.writeJson(this.promptsPath, prompts, { spaces: 2 });
    
    return newPrompt;
  }

  async update(id: string, updates: Partial<Prompt>): Promise<Prompt> {
    const prompts = await this.list();
    const index = prompts.findIndex(prompt => prompt.id === id);
    
    if (index === -1) {
      throw new Error('Prompt 不存在');
    }

    // 如果更新名称，检查是否重复
    if (updates.name && updates.name !== prompts[index].name) {
      if (prompts.some(prompt => prompt.name === updates.name && prompt.id !== id)) {
        throw new Error('Prompt 名称已存在');
      }
    }

    const updatedPrompt = {
      ...prompts[index],
      ...updates,
      id,
      created: prompts[index].created,
      updated: new Date().toISOString()
    };

    prompts[index] = updatedPrompt;
    await fs.writeJson(this.promptsPath, prompts, { spaces: 2 });
    
    return updatedPrompt;
  }

  async delete(id: string): Promise<void> {
    const prompts = await this.list();
    const filteredPrompts = prompts.filter(prompt => prompt.id !== id);
    
    if (filteredPrompts.length === prompts.length) {
      throw new Error('Prompt 不存在');
    }

    await fs.writeJson(this.promptsPath, filteredPrompts, { spaces: 2 });
  }

  // 增加使用次数
  async incrementUsage(id: string): Promise<void> {
    const prompts = await this.list();
    const prompt = prompts.find(p => p.id === id);
    
    if (prompt) {
      prompt.usageCount++;
      prompt.updated = new Date().toISOString();
      await fs.writeJson(this.promptsPath, prompts, { spaces: 2 });
    }
  }

  // 评分
  async rate(id: string, rating: number): Promise<void> {
    if (rating < 1 || rating > 5) {
      throw new Error('评分必须在 1-5 之间');
    }

    const prompts = await this.list();
    const prompt = prompts.find(p => p.id === id);
    
    if (prompt) {
      prompt.rating = rating;
      prompt.updated = new Date().toISOString();
      await fs.writeJson(this.promptsPath, prompts, { spaces: 2 });
    }
  }
}